import { FormNumberInput } from "@/components/atom/numberInput/form/FormNumberInput";
import { FormTextInput } from "@/components/atom/textInput/form/FormTextInput";
import { MantineSelectableTable } from "@/components/molecule/mantineSelectableTable/MantineSelectableTable";
import FormTemplate from "@/components/template/form/FormTemplate";
import { COMMON_TEXT } from "@/constants/text";
import { useModal } from "@/context/ModalStackManager";
import { useInventoriesFormFetch } from "@/fetch/form/useInventoriesFormFetch";
import { useBarcodePrintHandler } from "@/hooks/handler/useBarcodePrintHandler";
import { useRawMaterialReturnHandler } from "@/hooks/handler/useRawMaterialReturnHandler";
import { useNotification } from "@/hooks/useNotification";
import { useWorkDataStore } from "@/store/work.store";
import { Flex } from "@mantine/core";
import { useForm } from "@mantine/form";
import { MRT_ColumnDef, MRT_Row } from "mantine-react-table";
import { useEffect } from "react";

type FormType = {
  order: number;
  lotId: number;
  itemCode: string;
  itemName: string;
  lotName: string;
  lotExpireDate: string;
  locationCode: string;
  quantity: string | undefined;
};

const RawMaterialReturn = Object.assign(
  {},
  {
    Template: FormTemplate,
    TextInput: FormTextInput,
    Table: MantineSelectableTable,
    NumberINput: FormNumberInput,
  },
);

export const RawMaterialReturnForm = () => {
  const { closeModal } = useModal();
  const { workData } = useWorkDataStore((state) => ({
    workData: state.workData,
  }));
  const { work } = workData;
  const { notifyRawMaterialReturnModal } = useNotification();

  const form = useForm({
    initialValues: {
      worksBulkInputPutRequest: [] as FormType[],
    },
  });

  const lotName = form.values.worksBulkInputPutRequest.map(
    (lot) => lot.lotName,
  );
  const itemCode = form.values.worksBulkInputPutRequest.map(
    (item) => item.itemCode,
  );

  const bomItemCodes = work?.routingData?.routingBoms?.map(
    (value) => value.itemCode,
  );

  // 변환이기 떄문에 위치가 바뀌어야함 (from -> to)
  const sourceLocationCode = work?.locationSetting?.toLocation?.code;

  // 변환이기 떄문에 위치가 바뀌어야함 (to -> from)
  const targetLocationCode = work?.locationSetting?.fromLocation?.code;

  const { data } = useInventoriesFormFetch({
    itemCodes: bomItemCodes,
    locationCode: sourceLocationCode,
  });

  const targetLocationCodes = form.values.worksBulkInputPutRequest.map(
    () => targetLocationCode,
  );

  const { onSubmit } = useRawMaterialReturnHandler();
  const { onSubmit: onBarcodePrint } = useBarcodePrintHandler({
    lotName: lotName,
    itemCode: itemCode,
    locationCode: targetLocationCodes as string[],
  });

  const columns = () =>
    [
      {
        accessorKey: "order",
        header: COMMON_TEXT.COLUMN.ORDER,
      },
      {
        accessorKey: "itemCode",
        header: COMMON_TEXT.COLUMN.ITEM_CODE,
      },
      {
        accessorKey: "itemName",
        header: COMMON_TEXT.COLUMN.ITEM_NAME,
      },
      {
        accessorKey: "lotName",
        header: COMMON_TEXT.COLUMN.LOT_NAME,
      },
      {
        accessorKey: "lotExpiration",
        header: COMMON_TEXT.COLUMN.LOT_EXPIRATION_DATE,
      },
      {
        accessorKey: "locationCode",
        header: COMMON_TEXT.COLUMN.LOT_LOCATION,
      },
      {
        accessorKey: "quantity",
        header: COMMON_TEXT.COLUMN.QUANTITY,
      },
    ] as MRT_ColumnDef<any>[];

  const handleRowDelete = (row: MRT_Row<any>) => {
    form.removeListItem("worksBulkInputPutRequest", row.index);
  };

  const handleReturn = async () => {
    const result = await notifyRawMaterialReturnModal();

    const workLotPairs = form.values.worksBulkInputPutRequest.map((data) => ({
      workId: workData.work?.id as number,
      lotId: data.lotId as number,
      targetLocationCode,
      sourceLocationCode,
      quantity: data.quantity,
      lotName: data.lotName,
    }));

    // 원부자재 반환 요청
    await onSubmit({ workLotPairs });
    // 바코드 출력 여부
    if (result) {
      await onBarcodePrint();
    }
  };

  useEffect(() => {
    if (!data?.data) return;

    const rowsWithLotId = data.data.filter((row) => row.lotId !== undefined);
    const convertedData = rowsWithLotId.map((row, index) => ({
      order: index + 1,
      lotId: row.lotId as number, // lotId가 undefined가 아님을 보장
      itemCode: row.itemCode ?? "-", // 기본값 설정
      itemName: row.itemName ?? "-", // 기본값 설정
      lotName: row.lot?.name ?? "-",
      lotExpireDate: row.lot?.expiration ?? "-",
      locationCode: row.locationCode ?? "-", // 기본값 설정
      quantity: row.quantity, // 기본값 설정
    }));
    form.setValues({
      worksBulkInputPutRequest: convertedData,
    });
  }, [data]);

  return (
    <RawMaterialReturn.Template
      w={1000}
      label={COMMON_TEXT.BUTTON.RAW_MATERIAL_BULK_RETURN}
      onSubmit={handleReturn}
      onCancel={() => closeModal({})}
    >
      <Flex gap={"xl"}>
        <RawMaterialReturn.TextInput
          disabled
          value={sourceLocationCode}
          label={COMMON_TEXT.LABEL.FROM_LOCATION}
        />
        <RawMaterialReturn.TextInput
          value={targetLocationCode}
          disabled
          label={COMMON_TEXT.LABEL.TO_LOCATION}
        />
      </Flex>
      <RawMaterialReturn.Table
        enableRowActions={true}
        enableRowSelection={false}
        localization={{
          noRecordsToDisplay: COMMON_TEXT.DATA_EMPTY.NO_DATA,
        }}
        data={form.values.worksBulkInputPutRequest ?? []}
        columns={columns()}
        onClickDelete={handleRowDelete}
      />
    </RawMaterialReturn.Template>
  );
};
